import { defineStore } from "pinia";
import { AttributeOptionSet } from "./entity";
import { AttributeOptionSetList } from "./list.gen";
import type {
  AttributeOptionSetStore,
  AttributeOptionSetStoreState,
} from "./store.gen.d";
import { analytics, auth } from "@/firebase";
import { type JSONData } from "../../base_classes/dto.d";
import { Pagination } from "../../base_classes/pagination";
import { Query } from "../../base_classes/query";
import { paginationAndQueriesToCacheKey } from "../../base_classes/dto_list";

/// ///////////////////////////////////////////////////////////////////////////////////////////////////
/// WARNING! THIS FILE IS AUTOMATICALLY GENERATED, ANY CHANGES WILL BE LOST ON NEXT GENERATION
/// IF YOU WANT TO EDIT THIS FILE, REMOVE THE .gen. FROM THE FILENAME. THE FILE WILL THEN BE IGNORED.
/// ///////////////////////////////////////////////////////////////////////////////////////////////////

const attributeOptionSetList = new AttributeOptionSetList();
export const useAttributeOptionSetStore = defineStore(
  "attributeOptionSetStore",
  {
    state: (): AttributeOptionSetStoreState => ({
      activeID: null,
      cache: {},
      list: [] as AttributeOptionSet[],
      retrievalTimestamp: 0,
      updateKey: 0,
      storeUpdateTimeout: null,
    }),
    actions: {
      initializeStore(): void {
        this.convertList();
      },
      emitFormUpdateEvent(id: string): void {
        window.dispatchEvent(
          new CustomEvent("form-update-AttributeOptionSet", { detail: { id } }),
        );
      },
      emitStoreUpdateEvent(): void {
        // Clear existing timeout
        if (this.storeUpdateTimeout !== null) {
          clearTimeout(this.storeUpdateTimeout);
        }

        // Set new timeout
        this.storeUpdateTimeout = setTimeout(() => {
          window.dispatchEvent(
            new CustomEvent("store.update.AttributeOptionSet"),
          );
          this.storeUpdateTimeout = null;
        }, 1500);
      },
      async activate(attributeOptionSet: AttributeOptionSet): Promise<void> {
        // Set active
        await attributeOptionSet
          .activate(auth.firebaseUser?.uid)
          .then(() => {
            this.activeID = attributeOptionSet.id;
          })
          .catch((error) => {
            console.error(error);
            this.activeID = null;
          });
      },
      add(attributeOptionSet: AttributeOptionSet): void {
        const id = attributeOptionSet.id;
        if (typeof id === "string" && !this.contains(id)) {
          this.list.push(attributeOptionSet);

          // Set active
          if (this.active === null) {
            this.activeID = attributeOptionSet.id;
          }
        } else {
          this.reinsert(attributeOptionSet);
        }
        this.updateKey += 1;
      },
      convertList(): void {
        this.list = (this.list as AttributeOptionSet[]).map((x: any) => {
          const attributeOptionSet = new AttributeOptionSet({});
          attributeOptionSet.set(x as JSONData);
          return attributeOptionSet;
        });
      },
      async delete(attributeOptionSet: AttributeOptionSet): Promise<void> {
        await attributeOptionSet
          .delete()
          .then(() => {
            this.remove(attributeOptionSet);
          })
          .finally(() => {
            this.emitStoreUpdateEvent();
          });
      },
      async deleteById(id: string): Promise<void> {
        const attributeOptionSet = (this.list as AttributeOptionSet[]).find(
          (x: AttributeOptionSet) => x.id === id,
        );
        if (attributeOptionSet !== undefined) {
          await this.delete(attributeOptionSet);
        } else {
          throw new Error("Could not find AttributeOptionSet with id: " + id);
        }
      },
      contains(id: string): boolean {
        return (this.list as AttributeOptionSet[]).some(
          (x: AttributeOptionSet) => x.id === id,
        );
      },
      get(id: string): AttributeOptionSet | null {
        return (
          (this.list as AttributeOptionSet[]).find(
            (x: AttributeOptionSet) => x.id === id,
          ) || null
        );
      },
      reinsert(attributeOptionSet: AttributeOptionSet): void {
        const index = (this.list as AttributeOptionSet[]).findIndex(
          (x: AttributeOptionSet) => x.id === attributeOptionSet.id,
        );
        if (index !== -1) {
          this.list = [
            ...this.list.slice(0, index),
            attributeOptionSet,
            ...this.list.slice(index + 1),
          ];
        } else {
          this.add(attributeOptionSet);
        }
        this.updateKey += 1;
      },
      remove({ id }: AttributeOptionSet): void {
        const index = (this.list as AttributeOptionSet[]).findIndex(
          (x: AttributeOptionSet) => x.id === id,
        );
        if (index !== -1) {
          this.list = [
            ...this.list.slice(0, index),
            ...this.list.slice(index + 1),
          ];
        }
        this.updateKey += 1;
      },
      async retrieve(id: string, force = false): Promise<AttributeOptionSet> {
        // Check if a refresh is required
        if (!force && !this.refreshRequired) {
          const attributeOptionSet = this.get(id);
          if (attributeOptionSet !== null) {
            return attributeOptionSet;
          }
        }

        // Retrieve attributeOptionSet
        const attributeOptionSet = new AttributeOptionSet({});

        // Get attributeOptionSet
        await attributeOptionSet.get([id]).then(async (entity) => {
          this.reinsert(entity);

          // Activate attributeOptionSet
          await this.activate(entity);
        });

        return attributeOptionSet;
      },
      async retrieveAll(force = false): Promise<{
        active: string | null;
        items: AttributeOptionSet[];
      }> {
        // Check if a refresh is required
        if (!force && !this.refreshRequired) {
          return {
            active: this.activeID,
            items: this.list as AttributeOptionSet[],
          };
        }

        // Clear list
        while (this.list.length > 0) {
          this.list.pop();
        }

        // Retrieve list
        await attributeOptionSetList.getAll().then(async (list) => {
          list.forEach((attributeOptionSet) => {
            this.reinsert(attributeOptionSet);
          });

          // Activate attributeOptionSet
          if (list.length > 0) {
            if (attributeOptionSetList.active !== null) {
              this.activeID = attributeOptionSetList.active;
            } else {
              await this.activate(list[0]);
            }
          }
        });

        // Set retrieval timestamp
        this.retrievalTimestamp = Date.now();

        // Set user property
        if (this.activeID !== null) {
          analytics.setUserProperty("attribute_option_set_id", this.activeID);
        }

        return {
          active: this.activeID,
          items: this.list as AttributeOptionSet[],
        };
      },
      async retrievePaginated(
        pagination: Pagination,
        force = false,
      ): Promise<{
        pagination: Pagination;
        items: AttributeOptionSet[];
      }> {
        // Check pagination
        let newPagination = new Pagination(pagination).deepCopy();

        // Get cachekey
        const cacheKey = pagination.toCacheKey();

        // Check if a refresh is required
        if (!force && this.cache[cacheKey]) {
          const cached = this.cache[cacheKey];
          return {
            pagination: cached.pagination.deepCopy(),
            items: cached.ids.map(
              (id: string) => this.get(id) as AttributeOptionSet,
            ),
          };
        }

        // Retrieve list
        const items = await attributeOptionSetList
          .getPaginated([], pagination)
          .then(async ({ items, pagination }) => {
            items.forEach((attributeOptionSet) => {
              this.reinsert(attributeOptionSet);
            });

            // Set new pagination
            newPagination = pagination.deepCopy();

            return items;
          });

        // Set cache
        this.cache[cacheKey] = {
          ids: items.map((x: AttributeOptionSet) => x.id),
          pagination: newPagination,
          retrievalTimestamp: Date.now(),
        };

        // Set retrieval timestamp
        this.retrievalTimestamp = Date.now();

        return {
          pagination: newPagination,
          items,
        };
      },
      async query(
        queries: Query[],
        pagination: Pagination,
        force = false,
      ): Promise<{
        pagination: Pagination;
        items: AttributeOptionSet[];
      }> {
        // Check pagination
        let newPagination = new Pagination(pagination).deepCopy();

        // Get cachekey
        const cacheKey = paginationAndQueriesToCacheKey(pagination, queries);

        // Check if a refresh is required
        if (!force && this.cache[cacheKey]) {
          const cached = this.cache[cacheKey];
          return {
            pagination: cached.pagination.deepCopy(),
            items: cached.ids.map(
              (id: string) => this.get(id) as AttributeOptionSet,
            ),
          };
        }

        // Retrieve list
        const items = await attributeOptionSetList
          .query(queries, pagination)
          .then(async ({ items, pagination }) => {
            items.forEach((attributeOptionSet) => {
              this.reinsert(attributeOptionSet);
            });

            // Set new pagination
            newPagination = pagination.deepCopy();

            return items;
          });

        // Set cache
        this.cache[cacheKey] = {
          ids: items.map((x: AttributeOptionSet) => x.id),
          pagination: newPagination.deepCopy(),
          retrievalTimestamp: Date.now(),
        };

        // Set retrieval timestamp
        this.retrievalTimestamp = Date.now();

        return {
          pagination: newPagination,
          items: items,
        };
      },
      async search(
        query: string,
        pagination: Pagination,
        force = false,
      ): Promise<{
        pagination: Pagination;
        items: AttributeOptionSet[];
      }> {
        // Check pagination
        let newPagination = new Pagination(pagination).deepCopy();

        // Get cachekey
        const cacheKey =
          query.replaceAll(" ", "_") + "-" + pagination.toCacheKey();

        // Check if a refresh is required
        if (!force && this.cache[cacheKey]) {
          const cached = this.cache[cacheKey];
          return {
            items: cached.ids.map(
              (id: string) => this.get(id) as AttributeOptionSet,
            ),
            pagination: cached.pagination.deepCopy(),
          };
        }

        // Retrieve list
        const items = await attributeOptionSetList
          .search(query, pagination)
          .then(async ({ items, pagination }) => {
            items.forEach((attributeOptionSet) => {
              this.reinsert(attributeOptionSet);
            });

            // Set new pagination
            newPagination = pagination.deepCopy();

            return items;
          });

        // Set cache
        this.cache[cacheKey] = {
          ids: items.map((x: AttributeOptionSet) => x.id),
          pagination: newPagination.deepCopy(),
          retrievalTimestamp: Date.now(),
        };

        // Set retrieval timestamp
        this.retrievalTimestamp = Date.now();

        return {
          items: items,
          pagination: newPagination,
        };
      },
      async update(attributeOptionSet: AttributeOptionSet): Promise<string> {
        // Update attributeOptionSet
        await attributeOptionSet.update();

        // Reinsert attributeOptionSet
        this.reinsert(attributeOptionSet);

        // Activate attributeOptionSet
        if (this.active === null) {
          await this.activate(attributeOptionSet);
        }

        this.emitStoreUpdateEvent();

        return attributeOptionSet.id;
      },
    },
    getters: {
      active(): AttributeOptionSet | null {
        if (this.activeID === null) {
          return null;
        }

        const attributeOptionSet = (this.list as AttributeOptionSet[]).find(
          (x: AttributeOptionSet) => x.id === this.activeID,
        );
        if (attributeOptionSet === undefined) {
          return null;
        }

        return attributeOptionSet;
      },
      refreshRequired(): boolean {
        return (
          this.retrievalTimestamp === 0 ||
          this.retrievalTimestamp < Date.now() - 30 * 60000 ||
          this.list.length === 0
        );
      },
    },
  },
);

// Export a function to create a new instance of the store
export function createAttributeOptionSetStore(): AttributeOptionSetStore {
  const store = useAttributeOptionSetStore();
  // Run the initializeStore action when the store is created
  store.initializeStore();
  return store as unknown as AttributeOptionSetStore; // TODO: Fix this
}
